import { ref } from 'vue';
import directus from './useDirectus.js';
import { useCollectionsStore } from '../store/collections.js';

const PAGE_FIELDS = [
  'id',
  'space_id',
  'parent_page_id',
  'slug',
  'title',
  'status',
  'sort',
  'hero_config',
  'view_component',
  'date_created',
  'date_updated',
  'user_created',
  'user_updated'
];

const PAGE_TTL = 15 * 60 * 1000;

/**
 * Magic slug used for the implicit Space landing page that holds blocks for
 * a Space when no real pages exist. Hidden from the menu and the page tree.
 */
export const LANDING_SLUG = '__home';

/**
 * Fetch and manage pages for a given Space.
 * Pages are returned as a flat list; use `tree()` to get a nested structure.
 */
export function useSpacePages(spaceId) {
  const pages = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const cache = useCollectionsStore();

  const cacheKey = () => `orbit_space_pages_${spaceId.value ?? spaceId}`;

  async function load({ forceRefresh = false, includeDrafts = false } = {}) {
    loading.value = true;
    error.value = null;
    try {
      const id = spaceId.value ?? spaceId;
      if (!id) {
        pages.value = [];
        return pages.value;
      }
      const filter = {
        space_id: { _eq: id },
        ...(includeDrafts ? {} : { status: { _eq: 'published' } })
      };
      const { data } = await cache.fetch(
        cacheKey(),
        async () => {
          const result = await directus.items('orbit_space_pages').readByQuery({
            fields: PAGE_FIELDS,
            filter,
            sort: ['parent_page_id', 'sort', 'title'],
            limit: -1
          });
          return result.data || [];
        },
        { ttl: PAGE_TTL, forceRefresh }
      );
      pages.value = data;
    } catch (e) {
      error.value = e;
    } finally {
      loading.value = false;
    }
    return pages.value;
  }

  function tree() {
    // Return nested [{ ...page, children: [ ...pages ] }]
    const topLevel = pages.value
      .filter(p => !p.parent_page_id)
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
    return topLevel.map(p => ({
      ...p,
      children: pages.value
        .filter(c => c.parent_page_id === p.id)
        .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    }));
  }

  async function getPage(pageSlug, parentSlug = null, { forceRefresh = false } = {}) {
    if (!pages.value.length || forceRefresh) {
      await load({ forceRefresh, includeDrafts: true });
    }
    if (parentSlug) {
      const parent = pages.value.find(p => p.slug === parentSlug && !p.parent_page_id);
      if (!parent) return null;
      return pages.value.find(p => p.slug === pageSlug && p.parent_page_id === parent.id) || null;
    }
    return pages.value.find(p => p.slug === pageSlug && !p.parent_page_id) || null;
  }

  async function createPage(payload) {
    // Enforce 1-level nesting cap
    if (payload.parent_page_id) {
      const parent = pages.value.find(p => p.id === payload.parent_page_id);
      if (parent && parent.parent_page_id) {
        throw new Error('Pages can only be nested one level deep');
      }
    }
    const id = spaceId.value ?? spaceId;
    const created = await directus.items('orbit_space_pages').createOne({
      space_id: id,
      status: 'draft',
      sort: payload.sort ?? pages.value.length,
      ...payload
    });
    cache.invalidate(cacheKey());
    return created;
  }

  async function updatePage(id, patch) {
    if (patch.parent_page_id) {
      const parent = pages.value.find(p => p.id === patch.parent_page_id);
      if (parent && parent.parent_page_id) {
        throw new Error('Pages can only be nested one level deep');
      }
    }
    const updated = await directus.items('orbit_space_pages').updateOne(id, patch);
    cache.invalidate(cacheKey());
    return updated;
  }

  async function deletePage(id) {
    await directus.items('orbit_space_pages').deleteOne(id);
    cache.invalidate(cacheKey());
  }

  async function reorder(orderedIds) {
    // Persist order for top-level pages (or within a sibling group)
    await Promise.all(
      orderedIds.map((id, index) =>
        directus.items('orbit_space_pages').updateOne(id, { sort: index })
      )
    );
    cache.invalidate(cacheKey());
  }

  /**
   * Deep-clone a page: copies the page record + all its rows + all blocks
   * (with their files M2M junctions). The clone always lands as a draft.
   * Returns the new page.
   */
  async function duplicatePage(pageId) {
    const original = pages.value.find((p) => p.id === pageId);
    if (!original) throw new Error('Page not found');

    // Find a unique slug ("foo" → "foo-copy" → "foo-copy-2" → …)
    const baseSlug = original.slug;
    const siblingSlugs = new Set(
      pages.value
        .filter((p) => (p.parent_page_id ?? null) === (original.parent_page_id ?? null))
        .map((p) => p.slug)
    );
    let newSlug = `${baseSlug}-copy`;
    let n = 2;
    while (siblingSlugs.has(newSlug)) {
      newSlug = `${baseSlug}-copy-${n++}`;
    }

    // Create the new page
    const id = spaceId.value ?? spaceId;
    const newPage = await directus.items('orbit_space_pages').createOne({
      space_id: id,
      parent_page_id: original.parent_page_id ?? null,
      title: `${original.title} (copy)`,
      slug: newSlug,
      status: 'draft',
      sort: pages.value.length,
      hero_config: original.hero_config || null
    });

    // Fetch the source page's rows + blocks
    const [rowsRes, blocksRes] = await Promise.all([
      directus.items('orbit_page_rows').readByQuery({
        fields: ['id', 'page_id', 'sort', 'config'],
        filter: { page_id: { _eq: pageId } },
        sort: ['sort'],
        limit: -1
      }),
      directus.items('orbit_page_blocks').readByQuery({
        fields: [
          'id', 'page_id', 'row_id', 'type', 'span', 'config', 'style', 'sort',
          'files.id', 'files.sort', 'files.caption', 'files.directus_files_id'
        ],
        filter: { page_id: { _eq: pageId }, row_id: { _nnull: true } },
        sort: ['row_id', 'sort'],
        limit: -1
      })
    ]);
    const sourceRows = rowsRes.data || [];
    const sourceBlocks = blocksRes.data || [];

    // Recreate rows, capturing the new IDs so blocks can be reattached
    const oldRowIdToNew = new Map();
    for (const r of sourceRows) {
      const newRow = await directus.items('orbit_page_rows').createOne({
        page_id: newPage.id,
        sort: r.sort,
        config: r.config || {}
      });
      oldRowIdToNew.set(r.id, newRow.id);
    }

    // Recreate blocks under the new rows. After creation, recreate any
    // file junction rows so attached images/files come along.
    for (const b of sourceBlocks) {
      const newRowId = oldRowIdToNew.get(b.row_id);
      if (!newRowId) continue;
      const newBlock = await directus.items('orbit_page_blocks').createOne({
        page_id: newPage.id,
        row_id: newRowId,
        type: b.type,
        span: b.span,
        config: b.config || {},
        style: b.style || {},
        sort: b.sort
      });
      // Re-attach files via the M2M junction (point at the same directus_files_id)
      for (const f of b.files || []) {
        const fileId = f.directus_files_id?.id || f.directus_files_id;
        if (!fileId) continue;
        await directus.items('orbit_page_blocks_files').createOne({
          orbit_page_blocks_id: newBlock.id,
          directus_files_id: fileId,
          sort: f.sort ?? 0,
          caption: f.caption ?? null
        });
      }
    }

    cache.invalidate(cacheKey());
    return newPage;
  }

  return {
    pages,
    loading,
    error,
    load,
    tree,
    getPage,
    createPage,
    updatePage,
    deletePage,
    reorder,
    duplicatePage
  };
}
